import { useEffect, useState } from "react";
import client from "../api/client";
import { useNavigate } from "react-router-dom";
import SomaticHeatmapInsight from "../components/SomaticHeatmapInsight";
import InsightsHelpModal from "../components/InsightsHelpModal";

const NS_LABEL_CANNABIS = { 1: "Dysregulated", 2: "Anxious", 3: "Neutral", 4: "Grounded", 5: "Regulated" };
const NS_LABEL_MUSHROOM = { 1: "Dysregulated", 2: "Tender", 3: "Open", 4: "Integrating", 5: "Regulated" };

function nsLabel(val, isMushroom) {
  const map = isMushroom ? NS_LABEL_MUSHROOM : NS_LABEL_CANNABIS;
  return map[Math.round(val)] || "";
}

const SETTING_KEYWORDS = [
  { key: "nature", terms: ["forest", "nature", "outside", "outdoor", "garden", "park", "beach", "mountain", "river", "lake"] },
  { key: "home", terms: ["home", "house", "bedroom", "living room", "inside", "indoor", "apartment"] },
  { key: "alone", terms: ["alone", "solo", "by myself", "solitary"] },
  { key: "with others", terms: ["with", "friend", "partner", "group", "ceremony"] },
  { key: "night", terms: ["night", "evening", "dark", "midnight", "dusk"] },
  { key: "daytime", terms: ["day", "morning", "afternoon", "sunrise", "sunlight"] },
  { key: "music", terms: ["music", "playlist", "headphones", "sound"] },
  { key: "ceremony", terms: ["ceremony", "ritual", "sacred", "intentional"] },
];

const clusterSetAndSetting = (rows) => {
  const clusters = {};
  rows.forEach(row => {
    if (!row.session_timing) return;
    const text = row.session_timing.toLowerCase();
    SETTING_KEYWORDS.forEach(({ key, terms }) => {
      if (terms.some(t => text.includes(t))) {
        if (!clusters[key]) clusters[key] = { intention_sum: 0, ns_sum: 0, count: 0, sample_entry_id: null, matched_term: terms[0] };
        clusters[key].intention_sum += parseFloat(row.avg_intention_met || 0) * parseInt(row.session_count);
        clusters[key].ns_sum += parseFloat(row.avg_ns_delta || 0) * parseInt(row.session_count);
        clusters[key].count += parseInt(row.session_count);
        if (!clusters[key].sample_entry_id) clusters[key].sample_entry_id = row.sample_entry_id;
      }
    });
  });
  return Object.entries(clusters)
    .map(([key, data]) => ({
      setting: key,
      avg_intention: data.count > 0 ? data.intention_sum / data.count : 0,
      avg_ns_delta: data.count > 0 ? data.ns_sum / data.count : 0,
      session_count: data.count,
      sample_entry_id: data.sample_entry_id,
      matched_term: data.matched_term,
    }))
    .filter(r => r.session_count > 0)
    .sort((a, b) => b.avg_intention - a.avg_intention);
};

const DOSE_ORDER = ["Micro", "Low", "Moderate", "High"];

const heatmapColor = (val) => {
  if (val === null || val === undefined) return "rgba(255,255,255,0.04)";
  if (val >= 4.5) return "rgba(125,211,132,0.7)";
  if (val >= 3.5) return "rgba(125,211,132,0.4)";
  if (val >= 2.5) return "rgba(255,211,80,0.35)";
  return "rgba(211,125,125,0.4)";
};

// Shared section divider for visual breathing room
const Divider = () => (
  <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", margin: "8px 0 44px 0" }} />
);

function InsightsPage() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [medicineType, setMedicineType] = useState("all");
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);
  const isMushroom = medicineType === "mushroom";

  useEffect(() => {
    setLoading(true);
    const params = `?medicine_type=${medicineType}`;
    client.get(`/insights${params}`)
      .then(res => setInsights(res.data))
      .catch(() => setError("Unable to load insights."))
      .finally(() => setLoading(false));
  }, [medicineType]);

  if (loading) return <p>Loading insights...</p>;
  if (error) return <p>{error}</p>;
  if (!insights) return null;

  const hasData = (arr) => arr && arr.length > 0;

  const Bar = ({ value, max, positive }) => {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
      <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: "4px", height: "8px", width: "120px", overflow: "hidden", display: "inline-block", verticalAlign: "middle" }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: "4px", background: positive === false ? "rgba(211,125,125,0.8)" : "rgba(125,211,132,0.8)" }} />
      </div>
    );
  };

  const Row = ({ label, bar, value, count, filterParams, singleEntryId, sublabel }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
    <span style={{ minWidth: "140px", fontWeight: "500", fontSize: "14px" }}>
      {label}
      {sublabel && <span style={{ display: "block", fontSize: "11px", opacity: 0.45, fontWeight: "400" }}>{sublabel}</span>}
    </span>
    {bar}
    <span style={{ fontSize: "14px", minWidth: "60px" }}>{value}</span>
    <span
      onClick={() => {
        if (parseInt(count) == 1 && singleEntryId) {
          navigate(`/session/${singleEntryId}`);
        } else {
          const params = Object.fromEntries(Object.entries(filterParams || {}).filter(([_, v]) => v != null));
          navigate(`/entries/filtered?${new URLSearchParams({ ...params, from: "insights" }).toString()}`);
        }
      }}
      style={{ fontSize: "12px", opacity: 0.8, cursor: "pointer", textDecoration: "underline", color: "#7dd3e8" }}
    >
      {count} {count === 1 ? "session" : "sessions"}
    </span>
  </div>
);

  // Insight card with optional accent color for mushroom sections
  const InsightCard = ({ children, accent }) => (
    <div style={{
      background: accent === "mushroom" ? "rgba(180,125,232,0.07)" : "rgba(125,211,232,0.07)",
      borderLeft: `3px solid ${accent === "mushroom" ? "rgba(180,125,232,0.5)" : "rgba(125,211,232,0.4)"}`,
      borderRadius: "0 10px 10px 0",
      padding: "14px 18px",
      marginTop: "16px",
      fontSize: "13.5px",
      lineHeight: "1.75",
      opacity: 0.95,
    }}>
      {children}
    </div>
  );

  const SectionTitle = ({ children }) => (
    <h2 style={{ fontSize: "15px", fontWeight: "600", marginBottom: "4px", letterSpacing: "0.01em" }}>{children}</h2>
  );

  const SectionSubtitle = ({ children }) => (
    <p style={{ fontSize: "12px", opacity: 0.7, marginBottom: "14px", marginTop: "2px" }}>{children}</p>
  );

  const processSomaticOverlay = (rows) => {
    const zones = { improved: {}, worsened: {} };
    rows.forEach((row) => {
      let sensations = row.body_sensations;
      if (typeof sensations === "string") {
        try { sensations = JSON.parse(sensations); } catch { return; }
      }
      if (!Array.isArray(sensations)) return;
      sensations.forEach((item) => {
        const zone = typeof item === "string" ? item : item?.label || item?.zone || item?.name;
        if (!zone) return;
        const dir = row.direction;
        if (!zones[dir][zone]) zones[dir][zone] = 0;
        zones[dir][zone]++;
      });
    });
    const topZones = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([z]) => z);
    return { improved: topZones(zones.improved), worsened: topZones(zones.worsened) };
  };

  // ─── DATA PREP ────────────────────────────────────────────────────────────

  const maxMoodDelta = Math.max(
    ...(insights.moodByStrain || []).map(r => Math.abs(parseFloat(r.avg_mood_delta))),
    ...(insights.moodByTimeOfDay || []).map(r => Math.abs(parseFloat(r.avg_mood_delta))),
    1
  );

  const intentionSuccess = insights.intentionSuccessByTag || [];
  const topIntention = intentionSuccess.length > 0
    ? intentionSuccess.reduce((a, b) => parseFloat(a.success_rate) > parseFloat(b.success_rate) ? a : b)
    : null;

  const nsData = insights.nervousSystemTrend || null;
  const strainReg = insights.nervousSystemByStrain || [];
  const timeReg = insights.nervousSystemByTime || [];
  const intentionReg = insights.nervousSystemByIntention || [];
  const somaticRaw = insights.somaticOverlay || [];
  const difficultSessions = insights.difficultSessions || [];
  const shadowSessions = insights.shadowSessions || [];
  const somaticProcessed = processSomaticOverlay(somaticRaw);
  const hasSomaticData = somaticProcessed.improved.length > 0 || somaticProcessed.worsened.length > 0;
  
  const shadowIntegratedCount = shadowSessions.filter(s => s.was_integrated).length;
  const shadowTotal = shadowSessions.length;

  // Merge moodByStrain and nervousSystemByStrain into unified strain rows
  const moodStrainMap = {};
(insights.moodByStrain || []).forEach(r => {
  moodStrainMap[`${r.subcategory}|${r.medicine_type}`] = r;
});
  const intentionStrainMap = {};
(insights.intentionByStrain || []).forEach(r => {
  intentionStrainMap[`${r.subcategory}|${r.medicine_type}`] = r;
});
  const allStrainsRaw = [
  ...(insights.nervousSystemByStrain || []),
  ...(insights.moodByStrain || []),
  ...(insights.intentionByStrain || []),
];
  const strainRegMap = {};
(insights.nervousSystemByStrain || []).forEach(r => {
  strainRegMap[`${r.subcategory}|${r.medicine_type}`] = r;
});
const seenStrains = new Set();
const allStrains = allStrainsRaw
  .filter(r => {
    const key = `${r.subcategory}|${r.medicine_type}`;
    if (seenStrains.has(key)) return false;
    seenStrains.add(key);
    return true;
  })
  .map(r => ({ name: r.subcategory, medicine_type: r.medicine_type }));

const cannabisStrains  = allStrains.filter(s => s.medicine_type === 'cannabis');
const mushroomStrains  = allStrains.filter(s => s.medicine_type === 'mushroom');
  //strainReg.forEach(r => { strainRegMap[r.subcategory] = r; });

  // Merge timeReg and moodByTimeOfDay
  const moodTimeMap = {};
  (insights.moodByTimeOfDay || []).map(r => { moodTimeMap[r.time_of_day] = r; });
  const allTimes = [...new Set([
    ...timeReg.map(r => r.time_of_day),
    ...(insights.moodByTimeOfDay || []).map(r => r.time_of_day),
  ])];

  // Mushroom sections
  const doseRows = insights.doseResponse || [];
  const integrationData = insights.integrationTracking || null;
  const depthRows = insights.journeyDepth || [];
  const settingRows = insights.setAndSetting || [];
  const settingClusters = clusterSetAndSetting(settingRows).filter(c => c.avg_intention > 0);
  const heatmapRows = insights.speciesDoseHeatmap || [];
  const allSpecies = [...new Set(heatmapRows.map(r => r.species))];
  const heatmapGrid = {};
  heatmapRows.forEach(r => {
    if (!heatmapGrid[r.species]) heatmapGrid[r.species] = {};
    heatmapGrid[r.species][r.dose_range] = { avg: parseFloat(r.avg_intention_met), count: parseInt(r.session_count) };
  });
  const spacingRows = insights.journeySpacing || [];
  const spacingDates = spacingRows.map(r => new Date(r.journey_date)).sort((a, b) => b - a);
  const spacingGaps = [];
  for (let i = 0; i < spacingDates.length - 1; i++) {
    spacingGaps.push(Math.round((spacingDates[i] - spacingDates[i + 1]) / (1000 * 60 * 60 * 24)));
  }
  const avgGap = spacingGaps.length > 0 ? Math.round(spacingGaps.reduce((a, b) => a + b, 0) / spacingGaps.length) : null;
  const lastGap = spacingGaps[0] || null;
  const tooSoon = lastGap !== null && lastGap < 28;

  // ─── INSIGHT GENERATORS ───────────────────────────────────────────────────

  const generateNSInsight = () => {
  if (!nsData) return null;
  const delta = parseFloat(nsData.avg_delta);
  const positivePct = Math.round(parseFloat(nsData.positive_shift_pct));

  // Don't generate insight if we don't have valid NS data
  if (isNaN(delta) || isNaN(positivePct)) return null;

  const beforeLabel = nsLabel(parseFloat(nsData.avg_before), isMushroom);
  const afterLabel = nsLabel(parseFloat(nsData.avg_after), isMushroom);
  const sessionWord = parseInt(nsData.session_count) === 1 ? "session" : "sessions";

  if (delta >= 1.5) return `Across your last ${nsData.session_count} ${sessionWord}, you consistently land in a more regulated state. ${positivePct}% show a positive shift, moving from ${beforeLabel} toward ${afterLabel} on average. Your practice is working.`;
  if (delta >= 0.5) return `Your ${sessionWord} tend to move you toward regulation. You shift from ${beforeLabel} to ${afterLabel} on average, with ${positivePct}% of ${sessionWord} showing improvement. Keep logging to sharpen this picture.`;
  if (delta > 0) return `There's a mild regulation benefit across your last ${nsData.session_count} ${sessionWord}, a subtle shift from ${beforeLabel} toward ${afterLabel}. More entries will clarify whether this pattern holds.`;
  if (delta === 0) return `Your nervous system state tends to remain stable across sessions, neither gaining nor losing ground. Look at the strain and timing sections below for patterns that might explain why.`;
  return `Some sessions are leaving you more activated than when you started, shifting from ${beforeLabel} to ${afterLabel} on average. Review the ${isMushroom ? "species and dose" : "strain and method"} sections below to identify what's driving this.`;
};

  const generateStrainInsight = (strainList = allStrains) => {
  const strainWord = strainList.some(s => s.medicine_type === 'mushroom') ? "species" : "strain";
  const withNS     = strainList.filter(s => strainRegMap[`${s.name}|${s.medicine_type}`]);
  const withIntent = strainList.filter(s => intentionStrainMap[`${s.name}|${s.medicine_type}`]);
  const withMood   = strainList.filter(s => moodStrainMap[`${s.name}|${s.medicine_type}`]);

  if (withIntent.length > 0) {
    const best = withIntent.reduce((a, b) =>
      parseFloat(intentionStrainMap[`${a.name}|${a.medicine_type}`]?.avg_intention_met || 0) >
      parseFloat(intentionStrainMap[`${b.name}|${b.medicine_type}`]?.avg_intention_met || 0) ? a : b
    );
    const bestScore = parseFloat(intentionStrainMap[`${best.name}|${best.medicine_type}`]?.avg_intention_met || 0);
    const bestMood  = moodStrainMap[`${best.name}|${best.medicine_type}`];
    let text = `${best.name} produces your strongest intention fulfillment at ${bestScore.toFixed(1)}/5 avg`;
    if (bestMood) {
      const md = parseFloat(bestMood.avg_mood_delta);
      text += `, with a ${md >= 0 ? "+" : ""}${md.toFixed(2)} mood delta`;
    }
    text += ".";
    if (withNS.length > 0) {
      const bestNS = withNS.reduce((a, b) =>
        parseFloat(strainRegMap[`${a.name}|${a.medicine_type}`]?.avg_delta || 0) >
        parseFloat(strainRegMap[`${b.name}|${b.medicine_type}`]?.avg_delta || 0) ? a : b
      );
      const bestNSDelta = parseFloat(strainRegMap[`${bestNS.name}|${bestNS.medicine_type}`]?.avg_delta || 0);
      if (bestNSDelta > 0) text += ` ${bestNS.name} shows the strongest mood regulation shift (+${bestNSDelta.toFixed(2)} avg).`;
    }
    return text;
  }
  if (withNS.length > 0) {
    const best = withNS.reduce((a, b) =>
      parseFloat(strainRegMap[`${a.name}|${a.medicine_type}`]?.avg_delta || 0) >
      parseFloat(strainRegMap[`${b.name}|${b.medicine_type}`]?.avg_delta || 0) ? a : b
    );
    const bestDelta = parseFloat(strainRegMap[`${best.name}|${best.medicine_type}`]?.avg_delta || 0);
    let text = `${best.name} is your most regulating ${strainWord}`;
    if (bestDelta > 0) text += ` — producing a +${bestDelta.toFixed(2)} avg shift`;
    text += ".";
    return text;
  }
  if (withMood.length > 0) {
    const best = withMood.reduce((a, b) =>
      parseFloat(moodStrainMap[`${a.name}|${a.medicine_type}`]?.avg_mood_delta || 0) >
      parseFloat(moodStrainMap[`${b.name}|${b.medicine_type}`]?.avg_mood_delta || 0) ? a : b
    );
    const bestDelta = parseFloat(moodStrainMap[`${best.name}|${best.medicine_type}`]?.avg_mood_delta || 0);
    return `${best.name} produces the best mood shift of your logged ${strainWord}s (+${bestDelta.toFixed(2)} avg).`;
  }
  return null;
};

  const generateTimeInsight = () => {
    if (allTimes.length === 0) return null;
    const withNS = allTimes.filter(t => timeReg.find(r => r.time_of_day === t));
    if (withNS.length === 0) return null;
    const bestNS = timeReg.reduce((a, b) => parseFloat(a.avg_delta) > parseFloat(b.avg_delta) ? a : b);
    const bestMood = moodTimeMap[bestNS.time_of_day];
    let text = `${bestNS.time_of_day} is your most regulating time of day (+${parseFloat(bestNS.avg_delta).toFixed(2)} NS shift avg)`;
    if (bestMood) text += `, with a ${parseFloat(bestMood.avg_mood_delta) >= 0 ? "+" : ""}${parseFloat(bestMood.avg_mood_delta).toFixed(2)} mood delta to match`;
    text += ".";
    const worstNS = timeReg.reduce((a, b) => parseFloat(a.avg_delta) < parseFloat(b.avg_delta) ? a : b);
    if (worstNS.time_of_day !== bestNS.time_of_day && parseFloat(worstNS.avg_delta) < -0.2) {
      text += ` ${worstNS.time_of_day} sessions tend to leave you less regulated, consider whether that timing still serves you.`;
    }
    return text;
  };

  const generateIntentionSuccessInsight = () => {
    if (!topIntention) return null;
    const rate = Math.round(parseFloat(topIntention.success_rate));
    const count = parseInt(topIntention.total_sessions);
    const strainWord = isMushroom ? "species or preparation" : "strain or method";
    const lowestIntention = intentionSuccess.length > 1
      ? intentionSuccess.reduce((a, b) => parseFloat(a.success_rate) < parseFloat(b.success_rate) ? a : b)
      : null;
    let text = `Your strongest intention is ${topIntention.tag_name} — met ${rate}% of the time across ${count} ${count === 1 ? "session" : "sessions"}.`;
    if (lowestIntention && lowestIntention.tag_name !== topIntention.tag_name) {
      const lowRate = Math.round(parseFloat(lowestIntention.success_rate));
      text += ` ${lowestIntention.tag_name} has the most room to grow at ${lowRate}%, try varying your ${strainWord} or time of day for those sessions.`;
    }
    return text;
  };

  const generateIntentionRegInsight = () => {
    if (!hasData(intentionReg)) return null;
    const best = intentionReg[0];
    const worst = intentionReg[intentionReg.length - 1];
    const bestDelta = parseFloat(best.avg_delta);
    const worstDelta = parseFloat(worst.avg_delta);
    let text = `When your intention includes "${best.intention}", your nervous system shifts the most (+${bestDelta.toFixed(2)} avg) — suggesting this intention aligns well with how your body responds.`;
    if (worst.intention !== best.intention && worstDelta < -0.2) {
      text += ` Sessions framed around "${worst.intention}" tend toward mild dysregulation. It may be worth exploring what's underneath that intention before your next session.`;
    }
    return text;
  };

  const generateSomaticInsight = () => {
  if (!hasSomaticData) return null;
  if (somaticProcessed.improved.length > 0 && somaticProcessed.worsened.length > 0) {
    return isMushroom
      ? `${somaticProcessed.improved.join(", ")} activation shows up most in your integrated sessions. ${somaticProcessed.worsened.join(" and ")} activation frequently appears during your most activated or challenging moments, worth tracking as a somatic signal, not a sign something went wrong.`
      : `${somaticProcessed.improved.join(", ")} activation shows up most in your regulated sessions. ${somaticProcessed.worsened.join(" and ")} activation frequently appears when your nervous system is struggling, worth paying attention to as an early signal.`;
  }
  if (somaticProcessed.improved.length > 0) {
    return isMushroom
      ? `${somaticProcessed.improved.join(", ")} activation correlates with your most integrated sessions. Notice when these areas feel open or alive — it may be a reliable indicator the medicine is moving well.`
      : `${somaticProcessed.improved.join(", ")} activation correlates with your most regulated sessions. Notice when these areas feel open or activated — it may be a reliable indicator that the session is working.`;
  }
  return isMushroom
    ? `${somaticProcessed.worsened.join(" and ")} activation frequently appears during your more challenging moments. This pattern is worth tracking — not as something to avoid, but as information about where the work is happening.`
    : `${somaticProcessed.worsened.join(" and ")} activation frequently correlates with dysregulation. This pattern is worth tracking to catch difficult sessions earlier.`;
};

  const generateDoseInsight = () => {
    if (!hasData(doseRows)) return null;
    const best = [...doseRows].sort((a, b) => parseFloat(b.avg_intention_met || 0) - parseFloat(a.avg_intention_met || 0))[0];
    const bestNS = [...doseRows].filter(r => r.avg_ns_delta !== null).sort((a, b) => parseFloat(b.avg_ns_delta || 0) - parseFloat(a.avg_ns_delta || 0))[0];
    let text = `${best.dose_range} journeys produce your highest intention fulfillment at ${parseFloat(best.avg_intention_met).toFixed(1)}/5 avg.`;
    if (bestNS && bestNS.dose_range !== best.dose_range) {
      text += ` ${bestNS.dose_range} doses produce the strongest nervous system shifts (+${parseFloat(bestNS.avg_ns_delta).toFixed(2)} avg) — if regulation is the goal, that range may be worth exploring.`;
    } else {
      text += ` This range also produces your strongest nervous system regulation, a consistent sweet spot.`;
    }
    return text;
  };

  const generateIntegrationInsight = () => {
  if (!integrationData) return null;
  const total = parseInt(integrationData.total_journeys);
  const integrated = parseInt(integrationData.integrated_journeys);
  if (total === 0) return null;
  const pct = Math.round((integrated / total) * 100);

  if (pct === 0) return `No integration entries logged yet across ${total} ${total === 1 ? "journey" : "journeys"}. Integration is where insight becomes change. Even a few sentences in the days after a journey makes a difference.`;
  if (pct >= 75) return `You integrate ${pct}% of your journeys, a strong practice. The insights you're seeing here are built on that consistency. Keep it up.`;
  if (pct >= 40) return `${integrated} of ${total} journeys have an integration entry (${pct}%). The sessions you do integrate likely show stronger patterns. Logging even a few sentences after each journey compounds over time.`;
  return `Only ${pct}% of your journeys have integration entries. Integration is where insight becomes change. Try logging even briefly in the days after a journey to build that muscle.`;
};

  const generateDepthInsight = () => {
    if (depthRows.length < 2) return null;
    const recent = depthRows.slice(-3);
    const early = depthRows.slice(0, 3);
    const recentDeep = recent.reduce((sum, r) => sum + parseInt(r.deep_sessions), 0);
    const earlyDeep = early.reduce((sum, r) => sum + parseInt(r.deep_sessions), 0);
    if (recentDeep > earlyDeep) return `Your journeys are going deeper over time. Recent months show more shadow work, integration, and transformative sessions than when you started, a sign of growing capacity and trust.`;
    if (recentDeep === earlyDeep) return `Your journey depth has been consistent over time.`;
    return `Your recent journeys show fewer deep-work tags than earlier sessions. This may reflect a natural integration period, or an invitation to go deeper again when you're ready.`;
  };

  const generateSettingInsight = () => {
  if (!settingClusters || settingClusters.length === 0) return null;

  const best = settingClusters[0];
  const worst = settingClusters[settingClusters.length - 1];

  const formatSetting = (cluster) => {
    const name = cluster.setting.charAt(0).toUpperCase() + cluster.setting.slice(1);

    const ns =
      cluster.session_count < 2
        ? "too early to read NS patterns"
        : `${cluster.avg_ns_delta > 0 ? "+" : ""}${cluster.avg_ns_delta.toFixed(2)} NS shift`;

    return `• ${name}: ${cluster.avg_intention.toFixed(1)}/5 intention alignment — ${
      cluster.session_count === 1 ? "1 session" : `${cluster.session_count} sessions`
    }, ${ns}`;
  };

  const lines = [
    formatSetting(best),
    ...settingClusters.slice(1, -1).map(formatSetting),
    formatSetting(worst)
  ];

  const summary =
    "Nature settings show your clearest repeatable pattern. Night looks promising but needs more data. Sessions with others tend to be less internally focused.";

  return (
    <>
      {lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}

      <div style={{ marginTop: "12px" }}>{summary}</div>
    </>
  );
};

  const generateDifficultSessionsInsight = () => {
  if (difficultSessions.length === 0) return null;

  const lowIntent = difficultSessions.filter(s => parseInt(s.intention_met) <= 2).length;
  const negNS = difficultSessions.filter(s => parseFloat(s.ns_delta) < -0.5).length;

  let text = `You have ${difficultSessions.length} ${difficultSessions.length === 1 ? "session" : "sessions"} that moved in a more challenging direction`;

  if (lowIntent > 0 && negNS > 0) {
    text += `: ${lowIntent} with lower intention alignment and ${negNS} showing a downward nervous system shift.`;
  } else if (lowIntent > 0) {
    text += `: ${lowIntent} with lower intention alignment.`;
  } else {
    text += `: ${negNS} showing a downward nervous system shift.`;
  }

  text += ` These moments often carry important material and can be valuable to revisit with curiosity.`;

  return text;
};


const generateShadowInsight = () => {
  if (shadowSessions.length === 0) return null;
  let text = `Shadow Work, Ego Dissolution, and Surrender sessions carry a different kind of success metric. `;
  if (shadowTotal > 0) {
    const pct = Math.round((shadowIntegratedCount / shadowTotal) * 100);
    text += `${shadowIntegratedCount} of your ${shadowTotal} deep ${shadowTotal === 1 ? "session was" : "sessions were"} followed by an integration or after entry (${pct}%). `;
  }
  if (shadowIntegratedCount === shadowTotal && shadowTotal > 0) {
    text += `You're doing the work and closing the loop, that's rare and it matters.`;
  } else if (shadowIntegratedCount < shadowTotal) {
    text += `A difficult journey that you integrate fully is worth more than ten smooth ones. Writing even a few sentences after your hardest sessions helps your nervous system process what surfaced, turns raw experience into usable insight, and builds a record of your own patterns over time.`;
  }
  return text;
};

  // ─── EMPTY STATE CHECK ────────────────────────────────────────────────────
  const isEmpty = !hasData(intentionSuccess) &&
    (!nsData || parseInt(nsData.session_count) === 0) &&
    allStrains.length === 0 &&
    allTimes.length === 0 &&
    !hasData(intentionReg) &&
    !hasSomaticData &&
    !hasData(insights.entriesByState);

  

  

  return (
    <main className="InsightsPage" style={{ maxWidth: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
  <h1 style={{ margin: 0, width: "auto" }}>Pattern Insights</h1>
  <span
    onClick={() => setHelpOpen(true)}
    style={{
      cursor: "pointer",
      fontSize: "16px",
      opacity: 0.7,
      userSelect: "none",
      width: "24px",
    }}
  >
    ❔
  </span>
</div>

<InsightsHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />

{/* HEADER + TOGGLE */}
<div style={{ marginBottom: "32px" }}>
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: "12px",
      flexWrap: "wrap",
    }}
  >
    {/* LEFT: text column (flexible) */}
    <div style={{ flex: "1 1 60%", minWidth: 220 }}>
      <p style={{ opacity: 0.75, fontSize: "13px", margin: 0 }}>
        The more you journal, the more meaningful these patterns become.
      </p>

      <p style={{ opacity: 0.75, fontSize: "13px", margin: 0, marginTop: "8px" }}>
        Your Insights page looks across your recorded sessions to uncover patterns that can be difficult to notice one journey at a time. Over time, it helps surface how different variables—dose, setting, intention, and substance—shape your experience.
      </p>
    </div>
  </div>
</div>
      {isEmpty && (
        <p style={{ opacity: 0.85, fontStyle: "italic" }}>
          No patterns yet. Keep journaling and insights will appear here over time.
        </p>
      )}

      {/* ── 1. REGULATION TREND ── */}
{(() => {
  const fmt = (val) => (isNaN(parseFloat(val)) ? "—" : parseFloat(val).toFixed(1));
  return nsData && parseInt(nsData.session_count) > 0 && (
    <section style={{ marginBottom: "36px" }}>
      {/* Header row: title/subtitle on the left, filter buttons on the right */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", marginBottom: "12px" }}>
        <div>
          <SectionTitle>🧠 My Regulation Trend</SectionTitle>
          <SectionSubtitle>Last {nsData.session_count} sessions</SectionSubtitle>
        </div>

        <div style={{ display: "flex", gap: "8px", flex: "0 0 auto", alignItems: "center" }}>
          {[
            { value: "all", label: "🌿🌀 All" },
            { value: "cannabis", label: "🌿 Cannabis" },
            { value: "mushroom", label: "🌀 Psychedelics" },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => setMedicineType(opt.value)}
              style={{
                padding: "6px 14px",
                borderRadius: "20px",
                border: "1px solid rgba(125,211,232,0.3)",
                background: medicineType === opt.value ? "rgba(125,211,232,0.2)" : "transparent",
                color: medicineType === opt.value ? "#7dd3e8" : "rgba(255,255,255,0.7)",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: medicineType === opt.value ? 600 : 400,
                whiteSpace: "nowrap",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "16px" }}>
        {[{ label: "Avg Before", val: nsData.avg_before }, { label: "Avg After", val: nsData.avg_after }].map(({ label, val }) => (
          <div key={label} style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
            <div style={{ fontSize: "11px", opacity: 0.6, marginBottom: "4px" }}>{label}</div>
            <div style={{ fontSize: "18px", fontWeight: "500" }}>{fmt(val)}</div>
            <div style={{ fontSize: "11px", opacity: 0.7 }}>{nsLabel(parseFloat(val), isMushroom)}</div>
          </div>
        ))}
        <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
          <div style={{ fontSize: "11px", opacity: 0.6, marginBottom: "4px" }}>Avg Delta</div>
          <div style={{ fontSize: "18px", fontWeight: "500", color: parseFloat(nsData.avg_delta) >= 0 ? "#7dd3e8" : "#ff6b6b" }}>
            {!isNaN(parseFloat(nsData.avg_delta)) && parseFloat(nsData.avg_delta) > 0 ? "+" : ""}{fmt(nsData.avg_delta)}
          </div>
          <div style={{ fontSize: "11px", opacity: 0.7 }}>
            {isNaN(parseFloat(nsData.positive_shift_pct)) ? "—" : Math.round(parseFloat(nsData.positive_shift_pct)) + "% positive"}
          </div>
        </div>
      </div>

      {generateNSInsight() && <InsightCard>🧠 {generateNSInsight()}</InsightCard>}
    </section>
  );
})()}


      {/* ── 2. STRAIN / SPECIES (consolidated mood + NS + intention) ── */}
{allStrains.length > 0 && (() => {
  const renderStrainGroup = (strains, label, emoji, accent) => {
    if (strains.length === 0) return null;
    const maxNSDelta = Math.max(...strains.map(s => {
      const key = `${s.name}|${s.medicine_type}`;
      return Math.abs(parseFloat(strainRegMap[key]?.avg_delta || 0));
    }), 1);
    return (
      <section style={{ marginBottom: "36px" }} key={label}>
        <SectionTitle>{emoji} {label}</SectionTitle>
        <SectionSubtitle>
          {accent === 'mushroom'
            ? "Nervous system shift, mood delta, and intention score"
            : "Nervous system shift, mood delta, and intention score per strain"}
        </SectionSubtitle>
        {strains.map(({ name: strain, medicine_type: mt }) => {
          const key = `${strain}|${mt}`;
          const nsRow = strainRegMap[key];
          const moodRow   = moodStrainMap[key];
          const intentRow = intentionStrainMap[key];
          const singleId = nsRow?.single_entry_id || moodRow?.single_entry_id || intentRow?.single_entry_id;
          const delta        = nsRow     ? parseFloat(nsRow.avg_delta)             : null;
          const moodDelta    = moodRow   ? parseFloat(moodRow.avg_mood_delta)      : null;
          const intentMet    = intentRow ? parseFloat(intentRow.avg_intention_met) : null;
          const sessionCount = nsRow?.session_count || moodRow?.entry_count || intentRow?.entry_count || 0;
          const pattern      = delta === null ? "" : delta >= 1.5 ? "Strong Regulator" : delta >= 0.5 ? "Mild Grounding" : delta > 0 ? "Slight Improvement" : delta === 0 ? "Neutral" : delta > -0.5 ? "Slight Dysregulation" : "Dysregulating";
          const patternColor = delta === null ? "transparent" : delta >= 1.5 ? "#7dd3e8" : delta >= 0.5 ? "#a8e6a3" : delta >= -0.5 ? "rgba(255,255,255,0.45)" : "#ff9a9a";
          
          return (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap" }}>
              <span style={{ minWidth: "140px", fontWeight: "500", fontSize: "14px" }}>{strain}</span>
              {delta !== null && (
                <>
                  <Bar value={Math.abs(delta)} max={maxNSDelta} positive={delta >= 0} />
                  <span style={{ fontSize: "13px", minWidth: "58px", color: delta >= 0 ? "#7dd3e8" : "#ff9a9a" }}>
                    NS {delta > 0 ? "+" : ""}{delta}
                  </span>
                </>
              )}
              {moodDelta !== null && (
                <span style={{ fontSize: "13px", minWidth: "72px", color: moodDelta >= 0 ? "#a8e6a3" : "#ff9a9a", opacity: 0.85 }}>
                  mood {moodDelta > 0 ? "+" : ""}{moodDelta}
                </span>
              )}
              {intentMet !== null && (
                <span style={{ fontSize: "13px", opacity: 0.7 }}>
                  {intentMet.toFixed(1)}/5 intention
                </span>
              )}
              <span style={{ fontSize: "12px", color: patternColor, marginLeft: "auto" }}>{pattern}</span>
              <span
                onClick={() => {
                  if (parseInt(sessionCount) == 1 && singleId) {
                    navigate(`/session/${singleId}`);
                  } else {
                    navigate(`/entries/filtered?${new URLSearchParams({ subcategory: strain, medicine_type: mt }).toString()}`);
                  }
                }}
                style={{ fontSize: "12px", opacity: 0.7, cursor: "pointer", textDecoration: "underline", color: "#7dd3e8" }}
              >
                {sessionCount} {parseInt(sessionCount) === 1 ? "session" : "sessions"}
              </span>
            </div>
          );
        })}
        <InsightCard accent={accent}>{emoji} {generateStrainInsight(strains)}</InsightCard>
      </section>
    );
  };

  return (
    <>
      <Divider />
      {renderStrainGroup(cannabisStrains, "Strain Performance", "🌿", "cannabis")}
      {cannabisStrains.length > 0 && mushroomStrains.length > 0 && <div style={{ marginBottom: "24px" }} />}
      {renderStrainGroup(mushroomStrains, "Shifts", "🌀", "psychedelics")}
    </>
  );
})()}

      {/* ── 3. TIME OF DAY (consolidated mood + NS) ── */}
      {allTimes.length > 0 && (
        <>
          <Divider />
          <section style={{ marginBottom: "36px" }}>
            <SectionTitle>⏰ Time of Day</SectionTitle>
            <SectionSubtitle>Nervous system shift and mood delta by time of day</SectionSubtitle>
            {(() => {
              const maxNSDelta = Math.max(...timeReg.map(r => Math.abs(parseFloat(r.avg_delta))), 1);
              return allTimes.map((time) => {
                const nsRow = timeReg.find(r => r.time_of_day === time);
                const moodRow = moodTimeMap[time];
                const nsDelta = nsRow ? parseFloat(nsRow.avg_delta) : null;
                const moodDelta = moodRow ? parseFloat(moodRow.avg_mood_delta) : null;
                
                const sessionCount = nsRow?.session_count || moodRow?.entry_count || 0;
                const singleId = nsRow?.single_entry_id || moodRow?.single_entry_id;
                return (
                  <div key={time} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap" }}>
                    <span style={{ minWidth: "100px", fontWeight: "500", fontSize: "14px" }}>{time}</span>
                    {nsDelta !== null && (
                      <>
                        <Bar value={Math.abs(nsDelta)} max={maxNSDelta} positive={nsDelta >= 0} />
                        <span style={{ fontSize: "13px", minWidth: "60px", color: nsDelta >= 0 ? "#7dd3e8" : "#ff9a9a" }}>
                          NS {nsDelta > 0 ? "+" : ""}{nsDelta}
                        </span>
                      </>
                    )}
                    {moodDelta !== null && (
                      <span style={{ fontSize: "13px", minWidth: "72px", color: moodDelta >= 0 ? "#a8e6a3" : "#ff9a9a", opacity: 0.85 }}>
                        mood {moodDelta > 0 ? "+" : ""}{moodDelta}
                      </span>
                    )}
                    <span
                      onClick={() => {
                        if (parseInt(sessionCount) == 1 && singleId) {
                          navigate(`/session/${singleId}`);
                        } else {
                          navigate(`/entries/filtered?${new URLSearchParams({ time_of_day: time }).toString()}`);
                      }
                      }}
                      style={{ fontSize: "12px", opacity: 0.7, cursor: "pointer", textDecoration: "underline", color: "#7dd3e8", marginLeft: "auto" }}
                    >
                      {sessionCount} {parseInt(sessionCount) === 1 ? "session" : "sessions"}
                    </span>
                  </div>
                );
              });
            })()}
            <InsightCard>⏰ {generateTimeInsight()}</InsightCard>
          </section>
        </>
      )}

      {/* ── 4. INTENTION SUCCESS RATE ── */}
      {hasData(intentionSuccess) && (
        <>
          <Divider />
          <section style={{ marginBottom: "36px" }}>
            <SectionTitle>🎯 Intention Success Rate</SectionTitle>
            <SectionSubtitle>How often each intention is met (rated 4 or 5 out of 5)</SectionSubtitle>
            {intentionSuccess.map((row) => {
              const rate = parseFloat(row.success_rate);
              return (
                <Row key={row.tag_name} label={row.tag_name}
                  bar={<Bar value={rate} max={100} positive={rate >= 50} />}
                  singleEntryId={row.single_entry_id}
                  value={`${Math.round(rate)}%`}
                  count={parseInt(row.total_sessions)}
                  filterParams={{ intention_tag: row.tag_name }} />
              );
            })}
            {topIntention && <InsightCard>💡 {generateIntentionSuccessInsight()}</InsightCard>}
          </section>
        </>
      )}

      {/* ── 5. INTENTION + REGULATION ── */}
      {hasData(intentionReg) && (
        <>
          <Divider />
          <section style={{ marginBottom: "36px" }}>
            <SectionTitle>🎯 Intention + Nervous System</SectionTitle>
            <SectionSubtitle>How your nervous system responds based on what you intended going in</SectionSubtitle>
            {(() => {
              const maxDelta = Math.max(...intentionReg.map(r => Math.abs(parseFloat(r.avg_delta))), 1);
              return intentionReg.map((row) => {
                const delta = parseFloat(row.avg_delta);
                return (
                  <div key={row.intention} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <span style={{ minWidth: "160px", fontWeight: "500", fontSize: "14px" }}>
                      "{row.intention.length > 22 ? row.intention.slice(0, 22) + "…" : row.intention}"
                    </span>
                    <Bar value={Math.abs(delta)} max={maxDelta} positive={delta >= 0} />
                    <span style={{ fontSize: "14px", minWidth: "80px", color: delta >= 0 ? "#7dd3e8" : "#ff9a9a" }}>{delta > 0 ? "+" : ""}{delta} avg</span>
                    <span
                      onClick={() => {
                        if (parseInt(row.session_count) === 1 && row.single_entry_id) {
                          navigate(`/session/${row.single_entry_id}`);
                        } else {
                          navigate(`/entries/filtered?${new URLSearchParams({ intention: row.intention }).toString()}`);
                        }
                      }}
                      style={{ fontSize: "12px", opacity: 0.8, cursor: "pointer", textDecoration: "underline", color: "#7dd3e8" }}
                    >
                      {row.session_count} {parseInt(row.session_count) === 1 ? "session" : "sessions"}
                    </span>
                  </div>
                );
              });
            })()}
            <InsightCard>🎯 {generateIntentionRegInsight()}</InsightCard>
          </section>
        </>
      )}

      {/* ── 6. BODY ACTIVATION ── */}
      {(hasSomaticData || hasData(insights.somaticHeatmap)) && (
        <>
          <Divider />
          {hasSomaticData && (
            <section style={{ marginBottom: "28px" }}>
              <SectionTitle>🗺️ Body Activation Patterns</SectionTitle>
              <SectionSubtitle>Body zones correlated with regulation outcomes</SectionSubtitle>
              {somaticProcessed.improved.length > 0 && (
                <div style={{ marginBottom: "8px", fontSize: "14px" }}>
                  <span style={{ opacity: 0.6 }}>
                    {medicineType === "mushroom"
                      ? "When most regulated / integrated: "
                      : medicineType === "cannabis"
                      ? "When NS improves: "
                      : "When NS shifts upward: "}
                  </span>
                  <span style={{ color: "#7dd384" }}>{somaticProcessed.improved.join(", ")}</span>
                </div>
              )}
              {somaticProcessed.worsened.length > 0 && (
                <div style={{ marginBottom: "8px", fontSize: "14px" }}>
                  <span style={{ opacity: 0.7 }}>
                    {medicineType === "mushroom"
                      ? "When most activated / challenged: "
                      : medicineType === "cannabis"
                      ? "When NS worsens: "
                      : "When NS is more activated: "}
                  </span>
                  <span style={{ color: "#ff8f8f" }}>{somaticProcessed.worsened.join(", ")}</span>
                </div>
              )}
              <InsightCard>🗺️ {generateSomaticInsight()}</InsightCard>
            </section>
          )}
          {hasData(insights.somaticHeatmap) && (
            <section style={{ marginBottom: "36px" }}>
              <SectionTitle>🫁 Body Activation Heatmap</SectionTitle>
              <SomaticHeatmapInsight rows={insights.somaticHeatmap} medicineType={medicineType} />
            </section>
          )}
        </>
      )}

      {/* ── 7. MUSHROOM-ONLY SECTIONS ── */}
      {isMushroom && (
        <>
          {hasData(doseRows) && (
            <>
              <Divider />
              <section style={{ marginBottom: "36px" }}>
                <SectionTitle>🌀 Experience Patterns</SectionTitle>
                <SectionSubtitle>Reflections on intention fulfillment and shifts in your inner state — based on after/integration entries</SectionSubtitle>
                {doseRows.map(row => {
                  const nsD = parseFloat(row.avg_ns_delta);
                  const intent = parseFloat(row.avg_intention_met);
                  return (
                    <div key={row.dose_range} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <span style={{ minWidth: "100px", fontWeight: "500", fontSize: "14px" }}>{row.dose_range}</span>
                      <Bar value={intent} max={5} positive={true} />
                      <span style={{ fontSize: "13px", minWidth: "50px" }}>{intent.toFixed(1)}/5</span>
                      <span style={{ fontSize: "12px", color: nsD >= 0 ? "#7dd3e8" : "#ff9a9a", minWidth: "70px" }}>
                        NS {nsD > 0 ? "+" : ""}{isNaN(nsD) ? "—" : nsD.toFixed(2)}
                      </span>
                      <span
                        onClick={() => navigate(`/entries/filtered?${new URLSearchParams({ state: "After" }).toString()}`)}
                        style={{ fontSize: "11px", opacity: 0.7, cursor: "pointer", textDecoration: "underline", color: "#7dd3e8", marginLeft: "auto" }}
                    >
                      {row.session_count} {parseInt(row.session_count) === 1 ? "session" : "sessions"}
                    </span>
                    </div>
                  );
                })}
                <InsightCard accent="mushroom">🌀 {generateDoseInsight()}</InsightCard>
              </section>
            </>
          )}
          {integrationData && parseInt(integrationData.total_journeys) > 0 && (
            <>
              <Divider />
              <section style={{ marginBottom: "36px" }}>
                <SectionTitle>🌀 Integration Tracking</SectionTitle>
                <SectionSubtitle>How consistently you're logging after journeys</SectionSubtitle>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "16px" }}>
                  <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
                    <div style={{ fontSize: "11px", opacity: 0.6, marginBottom: "4px" }}>Total Journeys</div>
                    <div style={{ fontSize: "24px", fontWeight: "500" }}>{integrationData.total_journeys}</div>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
                    <div style={{ fontSize: "11px", opacity: 0.6, marginBottom: "4px" }}>Integrated</div>
                    <div style={{ fontSize: "24px", fontWeight: "500", color: "#7dd3e8" }}>{integrationData.integrated_journeys}</div>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
                    <div style={{ fontSize: "11px", opacity: 0.6, marginBottom: "4px" }}>Integration Rate</div>
                    <div style={{ fontSize: "24px", fontWeight: "500", color: "#a8e6a3" }}>
                      {Math.round((parseInt(integrationData.integrated_journeys) / parseInt(integrationData.total_journeys)) * 100)}%
                    </div>
                  </div>
                </div>
                <InsightCard accent="mushroom">🌀 {generateIntegrationInsight()}</InsightCard>
              </section>
            </>
          )}

          {/* ── DEPTH & DIFFICULTY (mushroom only) ── */}
{hasData(shadowSessions) && (
  <>
    <Divider />
    <section style={{ marginBottom: "36px" }}>
      <SectionTitle>🌑 Depth & Difficulty</SectionTitle>
      <SectionSubtitle>Sessions that asked something of you — shadow work, ego dissolution, struggle, and surrender</SectionSubtitle>
      {shadowSessions.map((row) => {
        const nsDelta = parseFloat(row.ns_delta);
        const intentMet = row.intention_met ? parseInt(row.intention_met) : null;
        const date = new Date(row.session_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const tags = Array.isArray(row.intention_tags) ? row.intention_tags : [];
        return (
          <div
            key={row.id}
            style={{
              padding: "12px 0",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              cursor: "pointer",
            }}
            onClick={() => navigate(`/entry/${row.id}`)}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: tags.length > 0 ? "8px" : "0" }}>
              <span style={{ fontSize: "12px", opacity: 0.7, minWidth: "110px" }}>{date}</span>
              {row.subcategory && (
                <span style={{ fontSize: "13px", fontWeight: "500" }}>{row.subcategory}</span>
              )}
              {row.amount && (
                <span style={{ fontSize: "12px", opacity: 0.65 }}>{row.amount}</span>
              )}
              {intentMet !== null && (
                <span style={{
                  fontSize: "12px",
                  color: intentMet <= 2 ? "#ff9a9a" : intentMet >= 4 ? "#a8e6a3" : "rgba(255,255,255,0.55)"
                }}>
                  intention {intentMet}/5
                </span>
              )}
              {!isNaN(nsDelta) && (
                <span style={{ fontSize: "12px", color: nsDelta < 0 ? "#ff9a9a" : "#7dd3e8" }}>
                  NS {nsDelta > 0 ? "+" : ""}{nsDelta.toFixed(1)}
                </span>
              )}
              <span style={{
                fontSize: "11px",
                marginLeft: "auto",
                color: row.was_integrated ? "#a8e6a3" : "rgba(255,255,255,0.3)",
                fontStyle: "italic"
              }}>
                {row.was_integrated ? "integrated ✓" : "not yet integrated"}
              </span>
              <span style={{ marginLeft: "auto", fontSize: "12px", color: "#7dd3e8", opacity: 0.7, cursor: "pointer", textDecoration: "underline" }}>view →</span>
            </div>
            {tags.length > 0 && (
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", paddingLeft: "122px" }}>
                {tags.map(tag => (
                  <span key={tag} style={{
                    fontSize: "11px",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: ['Shadow Work', 'Ego Dissolution', 'Trauma Processing'].includes(tag)
                      ? "rgba(180,125,232,0.15)"
                      : "rgba(255,255,255,0.07)",
                    color: ['Shadow Work', 'Ego Dissolution', 'Trauma Processing'].includes(tag)
                      ? "rgba(200,160,255,0.85)"
                      : "rgba(255,255,255,0.6)",
                    border: ['Shadow Work', 'Ego Dissolution', 'Trauma Processing'].includes(tag)
                      ? "1px solid rgba(180,125,232,0.25)"
                      : "none"
                  }}>{tag}</span>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <InsightCard accent="mushroom">🌑 {generateShadowInsight()}</InsightCard>
    </section>
  </>
)}



          {depthRows.length >= 2 && (
            <>
              <Divider />
              <section style={{ marginBottom: "36px" }}>
                <SectionTitle>🌊 Journey Depth Over Time</SectionTitle>
                <SectionSubtitle>Sessions tagged with shadow work, integration, or ego dissolution intentions</SectionSubtitle>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
                  {depthRows.map(row => {
                    const total = parseInt(row.total_sessions);
                    const deep = parseInt(row.deep_sessions);
                    const pct = total > 0 ? deep / total : 0;
                    return (
                      <div key={row.month} style={{ textAlign: "center", minWidth: "60px" }}>
                        <div style={{ height: "60px", width: "36px", margin: "0 auto", background: "rgba(255,255,255,0.06)", borderRadius: "4px", position: "relative", overflow: "hidden" }}>
                          <div style={{ position: "absolute", bottom: 0, width: "100%", height: `${Math.max(pct * 100, total > 0 ? 8 : 0)}%`, background: deep > 0 ? "rgba(180,125,232,0.6)" : "rgba(255,255,255,0.15)", borderRadius: "4px 4px 0 0" }} />
                        </div>
                        <div style={{ fontSize: "10px", opacity: 0.5, marginTop: "4px" }}>{row.month.split(" ")[0]}</div>
                        <div style={{ fontSize: "10px", opacity: 0.4 }}>{deep}/{total}</div>
                      </div>
                    );
                  })}
                </div>
                <InsightCard accent="mushroom">🌊 {generateDepthInsight()}</InsightCard>
              </section>
            </>
          )}

          {settingClusters.length > 0 && (
            <>
              <Divider />
              <section style={{ marginBottom: "36px" }}>
                <SectionTitle>🏕️ Set & Setting Patterns</SectionTitle>
                <SectionSubtitle>Clustered from your session timing and setting notes</SectionSubtitle>
                {settingClusters.map(cluster => (
                  <div key={cluster.setting} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <span style={{ minWidth: "120px", fontWeight: "500", fontSize: "14px", textTransform: "capitalize" }}>{cluster.setting}</span>
                    <Bar value={cluster.avg_intention} max={5} positive={true} />
                    <span style={{ fontSize: "13px", minWidth: "50px" }}>{cluster.avg_intention.toFixed(1)}/5</span>
                    <span style={{ fontSize: "12px", color: cluster.avg_ns_delta >= 0 ? "#7dd3e8" : "#ff9a9a", minWidth: "70px" }}>
                      NS {cluster.avg_ns_delta > 0 ? "+" : ""}{cluster.avg_ns_delta.toFixed(2)}
                    </span>
                    <span
                      onClick={() => {
                        if (cluster.session_count === 1 && cluster.sample_entry_id) {
                          navigate(`/session/${cluster.sample_entry_id}`);
                        } else {
                          navigate(`/entries/filtered?${new URLSearchParams({ session_timing_keyword: cluster.matched_term }).toString()}`);
                        }
                      }}
                    style={{ fontSize: "11px", opacity: 0.7, cursor: "pointer", textDecoration: "underline", color: "#7dd3e8", marginLeft: "auto" }}
                    >
                      {cluster.session_count} {cluster.session_count === 1 ? "session" : "sessions"}
                  </span>
                  </div>
                ))}
                <InsightCard accent="mushroom">{generateSettingInsight()}</InsightCard>
              </section>
            </>
          )}

          {allSpecies.length > 0 && (
            <>
              <Divider />
              <section style={{ marginBottom: "36px" }}>
                <SectionTitle>🗃️ Type × Amount Heatmap</SectionTitle>
                <SectionSubtitle>Avg intention met by type and amount range</SectionSubtitle>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", fontSize: "13px", width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: "8px 12px", opacity: 0.7, fontWeight: "400", fontSize: "11px" }}>Type</th>
                        {DOSE_ORDER.map(d => (
                          <th key={d} style={{ padding: "8px 12px", opacity: 0.7, fontWeight: "400", fontSize: "11px", textAlign: "center" }}>{d}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allSpecies.map(species => (
                        <tr key={species}>
                          <td style={{ padding: "8px 12px", fontWeight: "500" }}>{species}</td>
                          {DOSE_ORDER.map(dose => {
                            const cell = heatmapGrid[species]?.[dose];
                            return (
                              <td key={dose} style={{ padding: "6px", textAlign: "center" }}>
                                <div style={{ background: heatmapColor(cell?.avg), borderRadius: "6px", padding: "8px 4px", minWidth: "52px" }}>
                                  {cell ? (
                                    <>
                                      <div style={{ fontSize: "14px", fontWeight: "500" }}>{cell.avg.toFixed(1)}</div>
                                      <div style={{ fontSize: "10px", opacity: 0.7 }}>{cell.count}x</div>
                                    </>
                                  ) : (
                                    <div style={{ fontSize: "12px", opacity: 0.4 }}>—</div>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: "flex", gap: "16px", marginTop: "12px", fontSize: "11px", opacity: 0.6 }}>
                  {[
                    { color: "rgba(125,211,132,0.7)", label: "4.5+" },
                    { color: "rgba(125,211,132,0.4)", label: "3.5–4.5" },
                    { color: "rgba(255,211,80,0.35)", label: "2.5–3.5" },
                    { color: "rgba(211,125,125,0.4)", label: "below 2.5" },
                  ].map(({ color, label }) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <div style={{ width: "12px", height: "12px", borderRadius: "3px", background: color }} />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

          {spacingDates.length >= 2 && (
            <>
              <Divider />
              <section style={{ marginBottom: "36px" }}>
                <SectionTitle>📅 Journey Spacing</SectionTitle>
                <SectionSubtitle>Time between journeys — most integration frameworks suggest at least 4 weeks</SectionSubtitle>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
                  <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
                    <div style={{ fontSize: "11px", opacity: 0.6, marginBottom: "4px" }}>Avg Gap Between Journeys</div>
                    <div style={{ fontSize: "24px", fontWeight: "500" }}>{avgGap} days</div>
                  </div>
                  <div style={{ background: tooSoon ? "rgba(211,125,125,0.1)" : "rgba(255,255,255,0.05)", border: tooSoon ? "1px solid rgba(211,125,125,0.3)" : "none", borderRadius: "8px", padding: "12px", textAlign: "center" }}>
                    <div style={{ fontSize: "11px", opacity: 0.6, marginBottom: "4px" }}>Since Last Journey</div>
                    <div style={{ fontSize: "24px", fontWeight: "500", color: tooSoon ? "#ff9a9a" : "inherit" }}>{lastGap} days</div>
                  </div>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  {spacingDates.slice(0, 8).map((date, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: "13px" }}>
                      <span style={{ opacity: 0.7, minWidth: "100px" }}>{date.toLocaleDateString()}</span>
                      {i < spacingGaps.length && (
                        <span style={{ fontSize: "12px", color: spacingGaps[i] < 28 ? "#ff9a9a" : "#7dd3e8", opacity: 0.8 }}>
                          {spacingGaps[i]} days later
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <InsightCard accent="mushroom">
                  📅 {tooSoon
                    ? `Your last journey was only ${lastGap} days ago. Most integration frameworks suggest waiting at least 4 weeks between journeys to allow the nervous system to fully integrate. Your average gap is ${avgGap} days.`
                    : `Your average gap between journeys is ${avgGap} days. ${avgGap >= 28 ? "That's healthy spacing — enough time for real integration between sessions." : "Consider extending the time between journeys to allow fuller integration before going back in."}`
                  }
                </InsightCard>
              </section>
            </>
          )}
        </>
      )}

      {/* ── SESSIONS WORTH REVISITING (All + Cannabis) ── */}
{!isMushroom && hasData(difficultSessions) && (
  <>
    <Divider />
    <section style={{ marginBottom: "36px" }}>
      <SectionTitle>🌀 Sessions Worth Revisiting</SectionTitle>
      <SectionSubtitle>Sessions where the intention wasn't met or the nervous system struggled. Sometimes that's where the real work is.</SectionSubtitle>
      {difficultSessions.map((row) => {
        const nsDelta = parseFloat(row.ns_delta);
        const intentMet = row.intention_met ? parseInt(row.intention_met) : null;
        const date = new Date(row.session_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return (
          <div
            key={row.id}
            style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap", cursor: "pointer" }}
            onClick={() => navigate(`/entry/${row.id}`)}
          >
            <span style={{ minWidth: "110px", fontSize: "12px", opacity: 0.65 }}>{date}</span>
            {row.subcategory && (
              <span style={{ fontSize: "13px", fontWeight: "500", minWidth: "130px" }}>{row.subcategory}</span>
            )}
            {intentMet !== null && (
              <span style={{ fontSize: "12px", color: intentMet <= 2 ? "#ff9a9a" : "rgba(255,255,255,0.5)" }}>
                intention {intentMet}/5
              </span>
            )}
            {!isNaN(nsDelta) && (
              <span style={{ fontSize: "12px", color: nsDelta < -0.5 ? "#ff9a9a" : "rgba(255,255,255,0.5)" }}>
                NS {nsDelta > 0 ? "+" : ""}{nsDelta.toFixed(1)}
              </span>
            )}
            {hasData(row.intention_tags) && (
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {row.intention_tags.map(tag => (
                  <span key={tag} style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "12px", background: "rgba(255,255,255,0.07)", opacity: 0.7 }}>{tag}</span>
                ))}
              </div>
            )}
            <span style={{ marginLeft: "auto", fontSize: "12px", color: "#7dd3e8", opacity: 0.7, cursor: "pointer", textDecoration: "underline" }}>view →</span>
          </div>
        );
      })}
      <InsightCard>🌀 {generateDifficultSessionsInsight()}</InsightCard>
    </section>
  </>
)}


      {/* ── 8. SESSION STATES ── */}
      {hasData(insights.entriesByState) && (
        <>
          <Divider />
          <section style={{ marginBottom: "36px" }}>
            <SectionTitle>📓 Session States</SectionTitle>
            <SectionSubtitle>How often you journal before, during, and after sessions</SectionSubtitle>
            {(() => {
              const maxCount = Math.max(...insights.entriesByState.map(r => parseInt(r.entry_count)));
              return insights.entriesByState.map((row) => (
                <Row key={row.state}
                  label={row.state === "Before" ? "Before Session" : row.state === "During" ? "During Session" : row.state === "After" ? "After Session" : row.state}
                  bar={<Bar value={parseInt(row.entry_count)} max={maxCount} positive={true} />}
                  value={`${row.entry_count} ${parseInt(row.entry_count) === 1 ? "entry" : "entries"}`}
                  singleEntryId={row.single_entry_id}
                  count={parseInt(row.entry_count)}
                  filterParams={{ state: row.state }} />
              ));
            })()}
          </section>
        </>
      )}

    </main>
  );
}



export default InsightsPage;
